import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  Business as BusinessIcon,
  Groups as GroupsIcon,
  ExpandMore,
  Check,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectUser,
  selectProfile,
  selectSelectedTeam,
  selectCompany,
  setSelectedTeam
} from '../../store/authSlice';
import { logoutUser } from '../../store/authActions';

const Header = ({ title = "Dashboard" }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const profile = useSelector(selectProfile);
  const selectedTeam = useSelector(selectSelectedTeam);
  const company = useSelector(selectCompany);

  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [teamMenuAnchor, setTeamMenuAnchor] = useState(null);

  // Fallback teams data if none from Redux
  const displayTeams = profile?.data && profile?.data?.role.length > 0 ? profile?.data?.role : [
    { id: 'fallback', name: 'All Teams (Company)', type: 'Company' }
  ];

  // Auto-select first team if none selected and profile data is available
  useEffect(() => {
    if (profile?.data?.role?.length > 0 && !selectedTeam) {
      dispatch(setSelectedTeam(profile.data.role[0]));
    } else if (selectedTeam && profile?.data?.role?.length > 0) {
      // Validate that the selected team still exists in the current profile
      const teamExists = profile.data.role.some(team => team.teamId === selectedTeam.teamId);
      if (!teamExists) {
        dispatch(setSelectedTeam(profile.data.role[0]));
      }
    }
  }, [profile, selectedTeam, dispatch]);

  const displaySelectedTeam = selectedTeam || displayTeams[0];

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleTeamMenuOpen = (event) => {
    setTeamMenuAnchor(event.currentTarget);
  };

  const handleTeamMenuClose = () => {
    setTeamMenuAnchor(null);
  };

  const handleTeamSelect = (team) => {
    dispatch(setSelectedTeam(team));
    handleTeamMenuClose();
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    handleProfileMenuClose();
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
      <Toolbar sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1.5, py: 1.5 }}>
        {/* Top Row - Title and Profile */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {/* Left side - Title and Company */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
              {title}
            </Typography>
          
          {company && (
            <Chip
              icon={<BusinessIcon />}
              label={company.name || 'Company'}
              size="small"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          )}
        </Box>



        {/* Right side - Refresh and Profile Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Refresh Button */}
          <IconButton
            size="large"
            onClick={() => window.location.reload()}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
            title="Refresh Page"
          >
            <RefreshIcon />
          </IconButton>
          
          {/* Profile Menu */}
          <IconButton
            size="large"
            onClick={handleProfileMenuOpen}
            sx={{ color: 'white' }}
          >
            {profile?.avatar ? (
              <Avatar 
                src={profile.avatar} 
                sx={{ width: 32, height: 32 }}
              />
            ) : (
              <AccountCircle />
            )}
          </IconButton>
          
          <Menu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                minWidth: 220,
                mt: 1
              }
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {profile?.firstName && profile?.lastName 
                  ? `${profile.firstName} ${profile.lastName}`
                  : user?.username || 'User'
                }
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {profile?.email || user?.username}
              </Typography>
              {profile?.role && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {profile.role}
                </Typography>
              )}
            </Box>
            
            <Divider />
            
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
          </Box>
        </Box>
        
        {/* Team Filter Row */}
        <Box sx={{ width: '100%', px: 0, pb: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleTeamMenuOpen}
            endIcon={<ExpandMore />}
            sx={{
              justifyContent: 'space-between',
              px: 2,
              py: 1.2,
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.25)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.4)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {displaySelectedTeam?.teamType === 'P' ? (
                <BusinessIcon sx={{ fontSize: 18, color: 'white' }} />
              ) : (
                <GroupsIcon sx={{ fontSize: 18, color: 'white' }} />
              )}
              <Typography variant="body2" fontWeight="600" color="white">
                {displaySelectedTeam?.teamName || 'All Teams (Company)'}
              </Typography>
            </Box>
          </Button>
        </Box>
      </Toolbar>
      
      <Menu
        anchorEl={teamMenuAnchor}
        open={Boolean(teamMenuAnchor)}
        onClose={handleTeamMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            // width: '100%',
            minWidth: '380px',
            maxHeight: '300px',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            // Hide scrollbar but keep scroll functionality
            overflowY: 'auto',
            scrollbarWidth: 'none', // Firefox
            '&::-webkit-scrollbar': {
              display: 'none' // Chrome, Safari, Edge
            },
            '& ul': {
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {displayTeams.map((team, index) => (
          <MenuItem
            key={index}
            onClick={() => handleTeamSelect(team)}
            selected={displaySelectedTeam?.teamId === team.teamId}
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 1,
              mx: 0.5,
              mb: 0.5,
              '&:last-child': { mb: 0.5 },
              '&.Mui-selected': {
                backgroundColor: 'primary.50',
                color: 'primary.main',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'primary.100'
                }
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              {displaySelectedTeam?.teamId === team.teamId ? (
                <Check sx={{ fontSize: 18, color: 'primary.main' }} />
              ) : team.teamType === 'P' ? (
                <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              ) : (
                <GroupsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: displaySelectedTeam?.teamId === team.teamId ? 600 : 400 }}>
                  {team.teamName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {team.teamType === 'P' ? 'Company' : 'Team'}{team.accessType ? ` • ${team.accessType}` : ''}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </AppBar>
    </>
  );
};

export default Header;